import React, { useEffect, useMemo, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { services, localize } from '../mock';
import { Building2, Network, Layers, Scan, Box, HardHat, ArrowRight, Map, Eye, FileText, Image, Copy, Monitor, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import translations from '../translations';
import SEO from '../components/SEO';
import { SectionHero, SectionCTA, SectionHeading, ServiceCard } from '../components/Sections';

const iconMap = {
  Building2, Network, Layers, Scan, Box, HardHat, Map, Eye, FileText, Image, Copy, Monitor, ShieldCheck
};

const techIconMap = [Scan, Layers, Box, Monitor, ShieldCheck];

const FeaturedServiceCard = ({ service, idx, lang, t }) => {
  const IconComponent = iconMap[service.icon] || Building2;
  const benefits = t.serviceBenefits[service.id] || [];
  const localFeatures = lang === 'es' ? service.features_es : service.features;

  return (
    <Card className="border-2 border-orange-500 shadow-2xl overflow-hidden">
      <div className={`grid md:grid-cols-2 gap-0 ${idx % 2 === 1 ? 'md:grid-flow-dense' : ''}`}>
        <div className={`bg-gradient-to-br from-orange-500 to-orange-600 p-12 text-white flex flex-col justify-center ${idx % 2 === 1 ? 'md:col-start-2' : ''}`}>
          <div className="bg-white/10 backdrop-blur-sm w-20 h-20 rounded-2xl flex items-center justify-center mb-6">
            <IconComponent className="h-10 w-10 text-white" />
          </div>
          <h3 className="text-3xl font-bold mb-4">{localize(service, 'title', lang)}</h3>
          <p className="text-orange-50 text-lg mb-6">{localize(service, 'description', lang)}</p>
          <div className="space-y-3">
            {localFeatures.map((feature) => (
              <div key={feature} className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-white rounded-full"></div>
                <span className="text-orange-50">{feature}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={`p-12 bg-gray-50 flex flex-col justify-center ${idx % 2 === 1 ? 'md:col-start-1' : ''}`}>
          <h4 className="text-2xl font-bold text-gray-900 mb-6">{t.whyChooseService}</h4>
          <ul className="space-y-4">
            {benefits.map((benefit) => (
              <li key={benefit.title} className="flex items-start space-x-3">
                <div className="bg-orange-500 rounded-full p-1 mt-1 flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <span className="font-semibold text-gray-900">{benefit.title}:</span>
                  <span className="text-gray-600"> {benefit.description}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
};

const ProcessStep = ({ phase, isLast }) => (
  <div className="relative">
    <div className="text-center">
      <div className="bg-orange-500 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
        {phase.step}
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">{phase.title}</h3>
      <p className="text-gray-600 text-sm">{phase.description}</p>
    </div>
    {!isLast && (
      <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gray-300">
        <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
          <ArrowRight className="h-4 w-4 text-gray-300" />
        </div>
      </div>
    )}
  </div>
);

const TechItem = ({ item, icon: Icon }) => (
  <div className="flex items-center space-x-4">
    <div className="bg-orange-500 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
      <Icon className="h-6 w-6 text-white" />
    </div>
    <div>
      <h3 className="font-semibold text-gray-900">{item.title}</h3>
      <p className="text-gray-600 text-sm">{item.description}</p>
    </div>
  </div>
);

const Services = () => {
  const location = useLocation();
  const { lang } = useLanguage();
  const t = translations[lang].services;

  const scrollToHash = useCallback(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location.hash]);

  useEffect(() => {
    if (location.hash) {
      const timer1 = setTimeout(scrollToHash, 300);
      const timer2 = setTimeout(scrollToHash, 700);
      return () => { clearTimeout(timer1); clearTimeout(timer2); };
    }
    window.scrollTo(0, 0);
  }, [location.hash, scrollToHash]);

  const featuredServices = useMemo(() => services.filter(s => s.featured), [services]);
  const portfolioServices = useMemo(() => services.filter(s => !s.featured), [services]);

  return (
    <div className="min-h-screen">
      <SEO path="/services" />

      <SectionHero
        title1={t.heroTitle1}
        titleAccent={t.heroTitleAccent}
        title2={t.heroTitle2}
        description={t.heroDescription}
      />

      {/* Featured Services */}
      <section data-testid="featured-services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {t.featuredTag}
            </div>
            <SectionHeading title={t.signatureTitle} titleAccent={t.signatureTitleAccent} subtitle={t.signatureSubtitle} />
          </div>
          <div className="space-y-12">
            {featuredServices.map((service, idx) => (
              <FeaturedServiceCard key={service.id} service={service} idx={idx} lang={lang} t={t} />
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section id="service-portfolio" data-testid="service-portfolio" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading title={t.portfolioTitle} titleAccent={t.portfolioTitleAccent} subtitle={t.portfolioSubtitle} />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioServices.map((service) => {
              const IconComponent = iconMap[service.icon] || Building2;
              const localFeatures = lang === 'es' ? service.features_es : service.features;
              return (
                <ServiceCard
                  key={service.id}
                  icon={IconComponent}
                  title={localize(service, 'title', lang)}
                  description={localize(service, 'description', lang)}
                  features={localFeatures}
                  keyFeaturesLabel={t.keyFeatures}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section data-testid="process-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading title={t.processTitle} titleAccent={t.processTitleAccent} subtitle={t.processSubtitle} />
          <div className="grid md:grid-cols-4 gap-8">
            {t.processSteps.map((phase, index) => (
              <ProcessStep key={phase.step} phase={phase} isLast={index >= 3} />
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section data-testid="technology-section" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t.techTitle} <span className="text-orange-500">{t.techTitleAccent}</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8">{t.techDescription}</p>
              <div className="space-y-4">
                {t.techItems.map((item, index) => (
                  <TechItem key={item.title} item={item} icon={techIconMap[index]} />
                ))}
              </div>
            </div>
            <div>
              <img src="https://images.unsplash.com/photo-1618385455730-2571c38966b7" alt="BIM Technology" className="rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      <SectionCTA
        title={t.ctaTitle}
        description={t.ctaDescription}
        buttonText={t.requestQuote}
        buttonLink="/contact"
        testId="services-request-quote-btn"
      />
    </div>
  );
};

export default Services;
